import React, { useEffect, useState } from "react";
import { Tv, Pause, Play, DollarSign, Landmark, EyeOff, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { tvControlGet, tvControlSet, brokerPnl } from "../lib/api";
import { RemoteBusiness } from "./RemoteBusiness";
import { RemoteFacts } from "./RemoteFacts";
import { LOOKS } from "./NexusLooks";

export const SCENE_IDS = [
  ["mission", "Mission"], ["news", "News"], ["business", "Business"],
  ["newsroom", "Newsroom"], ["globe", "Globe"], ["living", "Living"],
];
const SCREEN_SCENES = {
  a: [["mission", "Mission"], ["floor-day", "Floor · Today"], ["reminders", "Reminders"], ["living", "Living"], ["anne-floor", "Floor"]],
  b: [["floor-day", "Floor · Today"], ["news", "News"], ["business", "Business"], ["newsroom", "Newsroom"], ["globe", "Globe"], ["intel", "Intel"], ["madmoney", "Mad Money"], ["channels", "Channels"], ["ira", "Roth IRA"]],
  floor: [["floor", "Floor"], ["v2", "Engine V2"], ["av-v3", "AV Trading v3"], ["hercules", "Hercules 2.0"], ["htu", "HTU"], ["htu-progress", "HTU Insights"], ["oakton", "Oakton"], ["oakton-growth", "Oakton Growth"], ["oakton-deals", "Oakton Deals"]],
};
const money$ = (v) => `${v < 0 ? "-" : ""}$${Math.abs(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function NexusRemote({ session }) {
  const uid = session.user_id;
  const [c, setC] = useState({ money: null, scene: null, paused: false, city: "" });
  const [city, setCity] = useState("");
  const [pnl, setPnl] = useState(null);
  const [tv, setTv] = useState("a");

  useEffect(() => { tvControlGet(uid).then((d) => { setC(d); setCity(d.city || ""); }).catch(() => {}); }, [uid]);
  const send = async (patch) => {
    const next = { money: c.money, scene: c.scene, paused: c.paused, variant: c.variant, screen: tv, ...patch };
    setC({ ...c, ...next });
    try { const d = await tvControlSet(uid, next); setC(d); } catch (e) { toast.error("TV didn't get that"); }
  };
  const toggleMoney = (m) => {
    const on = c.money === m ? null : m;
    send({ money: on });
    if (on === "pnl") brokerPnl(uid).then(setPnl).catch(() => {});
  };
  const btn = "rounded-2xl py-4 font-bold text-base flex items-center justify-center gap-2 transition-colors";

  return (
    <div className="min-h-screen bg-[#030712] text-cyan-50 p-5 pb-16 space-y-6 max-w-md mx-auto" data-testid="nexus-remote">
      <div className="flex items-center gap-3 pt-2"><Tv className="w-7 h-7 text-[#00F0FF]" /><h1 className="text-2xl font-black tracking-widest text-[#00F0FF]">TV REMOTE</h1></div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold">SCREEN</p>
          <div className="flex gap-1 bg-white/5 rounded-full p-1" data-testid="remote-tv-toggle">
            {["a", "b", "floor"].map((k) => (
              <button key={k} onClick={() => setTv(k)} data-testid={`remote-tv-${k}`}
                className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-widest ${tv === k ? "bg-[#00F0FF] text-[#030712]" : "text-cyan-200/60"}`}>TV {k.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {SCREEN_SCENES[tv].map(([id, label]) => (
            <button key={id} onClick={() => send({ scene: id, paused: true })} data-testid={`remote-scene-${id}`}
              className={`${btn} ${c.scene === id && c.paused ? "bg-[#00F0FF] text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}>{label}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {[[-1, "Previous", ChevronLeft], [1, "Next", ChevronRight]].map(([dir, label, I]) => (
            <button key={label} data-testid={`remote-scene-${label.toLowerCase()}`} className={`${btn} bg-white/5 border border-cyan-300/15`}
              onClick={() => { const L = SCREEN_SCENES[tv]; const k = L.findIndex(([id]) => id === c.scene); send({ scene: L[(k + dir + L.length) % L.length][0], paused: true }); }}>
              {dir < 0 && <I className="w-5 h-5" />}{label}{dir > 0 && <I className="w-5 h-5" />}</button>))}
        </div>
        <button onClick={() => send({ scene: c.scene, paused: !c.paused })} data-testid="remote-hold"
          className={`${btn} w-full ${c.paused ? "bg-amber-400 text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}>
          {c.paused ? <><Play className="w-5 h-5" /> Resume auto-rotate</> : <><Pause className="w-5 h-5" /> Hold this screen</>}
        </button>
      </section>

      <section className="space-y-3">
        <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold">NEXUS LOOK</p>
        <button onClick={() => tvControlSet(uid, { money: c.money, scene: c.scene, paused: c.paused, skip_intro: true }).then(() => toast.success("Intro skipped")).catch(() => {})}
          data-testid="remote-skip-intro" className={`${btn} w-full bg-white/5 border border-cyan-300/15`}>Skip boot intro</button>
        <div className="grid grid-cols-2 gap-2" data-testid="remote-looks">
          {LOOKS.map(([id, label]) => (
            <button key={id} onClick={() => send({ variant: id })} data-testid={`remote-variant-${id}`}
              className={`${btn} ${c.variant === id ? "bg-[#00F0FF] text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}>{label}</button>
          ))}
        </div>
        <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold pt-1">AUTO-CHANGE LOOK</p>
        <div className="grid grid-cols-4 gap-2" data-testid="remote-cycle">
          {[["off", "Off"], ["5", "5 min"], ["10", "10 min"], ["30", "30 min"]].map(([k, label]) => {
            const on = k === "off" ? !String(c.variant || "").startsWith("cycle") : c.variant === `cycle:${k}`;
            return (
              <button key={k} onClick={() => send({ variant: k === "off" ? "constellation" : `cycle:${k}` })} data-testid={`remote-cycle-${k}`}
                className={`rounded-xl py-3 text-sm font-bold transition-colors ${on ? "bg-[#E879F9] text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}>{label}</button>
            );
          })}
        </div>
        <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold pt-1">TV LAYOUT</p>
        <div className="grid grid-cols-2 gap-2" data-testid="remote-layout">
          {[["wall", "Wall (Nexus + screens)"], ["focus", "Focus (Nexus only)"]].map(([k, label]) => (
            <button key={k} onClick={() => send({ layout: k })} data-testid={`remote-layout-${k}`}
              className={`rounded-xl py-3 text-sm font-bold transition-colors ${c.layout === k ? "bg-[#00F0FF] text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}>{label}</button>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold">PRIVATE · SHOW ON TV</p>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => toggleMoney("pnl")} data-testid="remote-toggle-pnl" className={`${btn} ${c.money === "pnl" ? "bg-emerald-400 text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}><DollarSign className="w-5 h-5" /> P&L</button>
          <button onClick={() => toggleMoney("kalshi")} data-testid="remote-toggle-kalshi" className={`${btn} ${c.money === "kalshi" ? "bg-purple-400 text-[#030712]" : "bg-white/5 border border-cyan-300/15"}`}><Landmark className="w-5 h-5" /> Kalshi</button>
        </div>
        {c.money && <button onClick={() => send({ money: null })} data-testid="remote-hide-money" className={`${btn} w-full bg-rose-500/20 text-rose-200 border border-rose-400/30`}><EyeOff className="w-5 h-5" /> Hide from TV</button>}
        {c.money === "pnl" && pnl && (
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="remote-pnl-summary">
            <div className="bg-white/5 rounded-xl p-3"><p className="text-[10px] text-cyan-300/60">TODAY</p><p className={`font-black ${(pnl.day?.realized || 0) + (pnl.open?.unrealized || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{money$((pnl.day?.realized || 0) + (pnl.open?.unrealized || 0))}</p></div>
            <div className="bg-white/5 rounded-xl p-3"><p className="text-[10px] text-cyan-300/60">ACCOUNT</p><p className="font-black">{money$(pnl.equity)}</p></div>
            <div className="bg-white/5 rounded-xl p-3"><p className="text-[10px] text-cyan-300/60">CASH</p><p className="font-black">{money$(pnl.cash)}</p></div>
          </div>
        )}
      </section>

      <RemoteFacts />

      <section className="space-y-3">
        <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold">LOCAL NEWS & WEATHER CITY</p>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white/5 border border-cyan-300/15 rounded-xl px-3"><MapPin className="w-4 h-4 text-cyan-300/60" />
            <input value={city} onChange={(e) => setCity(e.target.value)} data-testid="remote-city-input" className="flex-1 bg-transparent py-3 outline-none" placeholder="City" /></div>
          <button onClick={() => send({ city }).then(() => toast.success("City updated"))} data-testid="remote-city-save" className="px-5 rounded-xl bg-[#00F0FF] text-[#030712] font-bold">Save</button>
        </div>
      </section>

      <RemoteBusiness userId={uid} />
    </div>
  );
}
